// 鋼材の「形状」を Excel 出力上のグループへ振り分けるためのロジック。
//
// なぜここで定義しているか:
// - 既存の MillsheetItem.sectionType は自由文字列（"H形鋼", "ROLLED STEEL H-BEAMS", "12GSHTB" 等）であり、
//   グルーピングや「厚・径」抽出のためには正規化が必要。
// - 同じロジックを (1) 初期データマイグレーション、(2) 将来の Excel 出力（形状別シート生成）の
//   両方で再利用するため、shared パッケージに集約する。
//
// 「厚・径」の参照位置は Notion 仕様（"使用鋼材一覧表のシート追加"）の表に基づく。
// BPO 部隊が後から手入力で訂正することを前提とした best-effort 抽出。

export const SECTION_TYPE_GROUPS = {
  PL_FB: 'PL_FB', // 鋼板 (PL) / 平鋼 (FB)
  HTB: 'HTB', // ボルト (高力ボルト 等)
  PIPE: 'PIPE', // 鋼管 (P / STK / STKN)
  SHAPE: 'SHAPE', // 角管 / 溝形 / 山形鋼
  H_BEAM: 'H_BEAM', // H形鋼 / SH / BH
  OTHER: 'OTHER',
} as const

export type SectionTypeGroupKey = (typeof SECTION_TYPE_GROUPS)[keyof typeof SECTION_TYPE_GROUPS]

export const sectionTypeGroupLabel: Record<SectionTypeGroupKey, string> = {
  PL_FB: '鋼板・平鋼',
  HTB: 'ボルト',
  PIPE: '鋼管',
  SHAPE: '角管・溝形・山形鋼',
  H_BEAM: 'H形鋼',
  OTHER: 'その他',
}

// HTB は H_BEAM より先に判定する必要がある（"12GSHTB" が "H" 系パターンに先に拾われないようにするため）。
const GROUP_RULES: Array<{ key: SectionTypeGroupKey; patterns: RegExp[] }> = [
  {
    key: SECTION_TYPE_GROUPS.HTB,
    patterns: [
      /HTB/,
      /ボルト/,
      /^F\d+T$/, // F8T 等の高力ボルト等級
      /^S\d+T$/, // S10T
    ],
  },
  {
    key: SECTION_TYPE_GROUPS.H_BEAM,
    patterns: [
      /^H$/,
      /^BH$/,
      /^SH$/,
      /^H形(鋼|綱)$/, // "H形綱" は実データに存在する誤字を含む
      /WIDE\s*FLANGE/,
      /H[\s-]*BEAM/,
      /H\s*SECTION/,
      /HISLEND/,
    ],
  },
  {
    key: SECTION_TYPE_GROUPS.PL_FB,
    patterns: [/^PL$/, /^SPL$/, /STEEL\s*PLATE/, /鋼板/, /^FB$/, /平鋼/],
  },
  // SHAPE は PIPE より先に判定する必要がある（"角形鋼管" が PIPE の /鋼管/ に拾われないようにするため）。
  {
    key: SECTION_TYPE_GROUPS.SHAPE,
    patterns: [
      /^L$/,
      /山形鋼/,
      /^C$/,
      /C形鋼/,
      /溝形/,
      /^\[/,
      /STKR/,
      /^BCP$/,
      /^BCR$/,
      /コラム/,
      /角\s*パイプ/,
      /角\s*型?\s*鋼管/,
      /角\s*形?\s*鋼管/,
    ],
  },
  {
    key: SECTION_TYPE_GROUPS.PIPE,
    patterns: [/^P$/, /^STK$/, /^STKN$/, /^STP$/, /鋼管/, /丸パイプ/],
  },
]

// sectionType 文字列から、振り分け先のグループを返す。
// 未知の値はすべて OTHER に倒す（誤分類を避ける安全側方針）。
export function resolveSectionTypeGroup(sectionType: string): SectionTypeGroupKey {
  const normalized = sectionType.trim().toUpperCase()
  if (normalized === '') {
    return SECTION_TYPE_GROUPS.OTHER
  }
  for (const { key, patterns } of GROUP_RULES) {
    for (const p of patterns) {
      if (p.test(normalized)) {
        return key
      }
    }
  }
  return SECTION_TYPE_GROUPS.OTHER
}

// Notion 仕様に基づく、グループごとの「size 文字列の何番目を抜き出すか」の定義（0-indexed）。
// OTHER は自動抽出対象外。
const GROUP_SIZE_INDICES: Record<SectionTypeGroupKey, readonly number[] | null> = {
  PL_FB: [0],
  HTB: [0],
  PIPE: [1],
  SHAPE: [0, 1],
  H_BEAM: [0, 1, 2],
  OTHER: null,
}

// size 文字列を「x / × / X / *」で分割し、各トークンが数値文字列ならその配列を返す。
// 先頭の非数値文字（"M22x85" の M、"L 75x75x6" の "L " 等）はあらかじめ除去する。
// 数値以外が混入していたら null（自動抽出を諦める）。
function tokenizeSize(size: string): string[] | null {
  const cleaned = size.replace(/^[^\d.]+/, '').trim()
  if (cleaned === '') {
    return null
  }
  const tokens = cleaned.split(/[x×X*]/).map((t) => t.trim())
  if (tokens.some((t) => t === '' || !/^\d+(?:\.\d+)?$/.test(t))) {
    return null
  }
  return tokens
}

// 指定グループの抽出ルールに従い、size から「厚・径」文字列を組み立てる。
// 複数位置を参照するグループは "x" で連結する（例: H_BEAM → "700x350x12"）。
export function parseThicknessDiameterFromSize(size: string, group: SectionTypeGroupKey): string | null {
  const indices = GROUP_SIZE_INDICES[group]
  if (!indices) {
    return null
  }
  const tokens = tokenizeSize(size)
  if (!tokens) {
    return null
  }
  const picked: string[] = []
  for (const i of indices) {
    const v = tokens[i]
    if (v === undefined) {
      return null
    }
    picked.push(v)
  }
  return picked.join('x')
}

// sectionType と size から「厚・径」を best-effort で推定する。
// グループ判定または size パースで失敗したら null を返す（手入力に委ねる）。
export function inferThicknessDiameter(sectionType: string, size: string): string | null {
  const group = resolveSectionTypeGroup(sectionType)
  return parseThicknessDiameterFromSize(size, group)
}
